/** Fixed, shell-free PAC Copilot reads and fresh-directory local exports. */
import { execFile } from "node:child_process";
import { lstat, mkdir, readdir, realpath } from "node:fs/promises";
import { basename, dirname, isAbsolute, join, resolve } from "node:path";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import { z } from "zod";
import { ApiError, AuthenticationError, ConfirmationRequiredError } from "../utils/errors.js";
import { GUID_PATTERN, sanitizeErrorMessage } from "../utils/security.js";

const TIMEOUT_MS = 60_000;
const MAX_OUTPUT_BYTES = 1024 * 1024;
const MAX_RETURN_CHARS = 24_000;
const environmentSchema = z
  .string()
  .min(1)
  .max(500)
  .describe(
    "Explicit Dataverse environment GUID or HTTPS Dataverse origin. PAC uses its own active auth profile, not the MCP sign-in or session environment.",
  );
const agentIdSchema = z.string().regex(GUID_PATTERN).describe("Exact agent GUID.");
const outputDirectorySchema = z
  .string()
  .min(1)
  .max(4096)
  .describe(
    "Absolute normalized path for a NEW directory. Its parent must already exist. Existing paths are never overwritten.",
  );

export const pacCopilotListSchema = z.object({ environment: environmentSchema }).strict();
export const pacCopilotExtractTemplateSchema = z
  .object({
    environment: environmentSchema,
    agentId: agentIdSchema,
    outputDirectory: outputDirectorySchema,
    confirmDownload: z.boolean().optional(),
  })
  .strict();
export const pacCopilotExtractTranslationSchema = pacCopilotExtractTemplateSchema.extend({
  allLanguages: z.boolean().optional(),
  format: z.enum(["json", "resx"]).optional(),
});

const environmentProperty = {
  type: "string",
  minLength: 1,
  maxLength: 500,
  description: environmentSchema.description,
};
const exportProperties = {
  environment: environmentProperty,
  agentId: { type: "string", pattern: GUID_PATTERN.source, description: "Exact agent GUID." },
  outputDirectory: {
    type: "string",
    minLength: 1,
    maxLength: 4096,
    description: outputDirectorySchema.description,
  },
  confirmDownload: {
    type: "boolean",
    description: "Required true to create local export files. No tenant changes.",
  },
};
const prerequisite =
  " Requires an installed PAC CLI and an already authenticated PAC profile; never signs in or changes profiles. Uses the explicit environment, not switch_environment. Command execution is bounded to 60 seconds.";

export const pacCopilotTools: Tool[] = [
  {
    name: "pac_copilot_list",
    description:
      "List Copilot Studio agents through the official PAC CLI. Read-only; returns bounded CLI output, not an invented JSON contract." +
      prerequisite,
    inputSchema: {
      type: "object",
      properties: { environment: environmentProperty },
      required: ["environment"],
      additionalProperties: false,
    },
  },
  {
    name: "pac_copilot_extract_template",
    description:
      "Download one Copilot Studio agent's official PAC YAML template into agent.yaml plus its compiled JSON companion in a NEW local directory. This reads the tenant and writes local files only; it does not create, edit, synchronize, or publish an agent. Requires confirmDownload=true and never overwrites existing paths." +
      prerequisite,
    inputSchema: {
      type: "object",
      properties: exportProperties,
      required: ["environment", "agentId", "outputDirectory"],
      additionalProperties: false,
    },
  },
  {
    name: "pac_copilot_extract_translation",
    description:
      "Extract one Copilot Studio agent's localized strings to JSON or RESX files in a NEW local directory using the official PAC CLI. Defaults to JSON and the primary language; allLanguages=true includes all supported languages. This does not merge translations into the tenant. Requires confirmDownload=true and never overwrites existing paths." +
      prerequisite,
    inputSchema: {
      type: "object",
      properties: {
        ...exportProperties,
        allLanguages: { type: "boolean", default: false },
        format: { type: "string", enum: ["json", "resx"], default: "json" },
      },
      required: ["environment", "agentId", "outputDirectory"],
      additionalProperties: false,
    },
  },
];

function validateEnvironment(value: string): string {
  if (GUID_PATTERN.test(value)) return value;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new ApiError("PAC environment must be a GUID or HTTPS Dataverse origin", 400);
  }
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.port ||
    url.pathname !== "/" ||
    url.search ||
    url.hash ||
    !/^[a-z0-9][a-z0-9-]*\.crm\d*\.dynamics\.com$/i.test(url.hostname)
  )
    throw new ApiError(
      "PAC environment must be an HTTPS Dataverse origin such as https://org.crm.dynamics.com (no path, credentials, or custom port)",
      400,
    );
  return url.origin;
}

function containsControlCharacter(value: string): boolean {
  return [...value].some(
    (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
  );
}

async function createExportDirectory(value: string): Promise<string> {
  if (!isAbsolute(value) || resolve(value) !== value || containsControlCharacter(value)) {
    throw new ApiError(
      "PAC outputDirectory must be an absolute normalized path without traversal or control characters",
      400,
    );
  }
  const parent = dirname(value);
  const metadata = await lstat(parent);
  if (!metadata.isDirectory() || metadata.isSymbolicLink()) {
    throw new ApiError("PAC output parent must be an existing real directory, not a symlink", 400);
  }
  const target = join(await realpath(parent), basename(value));
  try {
    // Atomic exclusive directory creation prevents an existing file, directory,
    // or symlink from becoming an overwrite target. Do not recursively mkdir.
    await mkdir(target, { mode: 0o700 });
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "EEXIST") {
      throw new ApiError(
        "PAC outputDirectory already exists; choose a new path. Existing content is never overwritten.",
        409,
      );
    }
    throw error;
  }
  return target;
}

function safePacText(text: string): string {
  // Preserve agent GUIDs and output locations on success, while redacting CLI
  // sign-in identities and any accidentally emitted credentials.
  return text
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, "[EMAIL]")
    .replace(/Bearer\s+\S+/gi, "Bearer [REDACTED]")
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[REDACTED JWT]")
    // PAC diagnostics may use plain assignments or JSON, not only URLs/JWTs.
    .replace(
      /(\b(?:(?:access|refresh|id)[_-]?token|client[_-]?secret|api[_-]?key|token|secret|password|pwd)\b["']?\s*[:=]\s*)(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^\s,;&}]+)/gi,
      "$1[REDACTED]",
    )
    .replace(/([?&](?:token|secret|password|sig|api[_-]?key)=)[^&\s]+/gi, "$1[REDACTED]");
}

async function verifyExport(directory: string, command: string): Promise<string> {
  const entries = await readdir(directory);
  if (entries.length === 0 || entries.length > 2000) {
    throw new ApiError(
      "PAC export did not produce a bounded nonempty set of local files. The output directory is retained for inspection.",
      502,
    );
  }
  if (
    command === "extract-template" &&
    (entries.length !== 2 ||
      !entries.includes("agent.yaml") ||
      !entries.includes("mcpExportTemplate-1.0.0.json"))
  ) {
    throw new ApiError(
      "PAC template export did not produce the expected agent.yaml and mcpExportTemplate-1.0.0.json files. Output is retained for inspection.",
      502,
    );
  }
  let totalBytes = 0;
  for (const name of entries) {
    const metadata = await lstat(join(directory, name));
    if (
      !metadata.isFile() ||
      metadata.isSymbolicLink() ||
      metadata.size === 0 ||
      (command === "extract-translation" && !/\.(?:json|resx)$/i.test(name))
    ) {
      throw new ApiError(
        "PAC export contains an empty, non-regular, or unexpected file. Output is retained for inspection.",
        502,
      );
    }
    totalBytes += metadata.size;
    if (totalBytes > 64 * 1024 * 1024) {
      throw new ApiError(
        "PAC export exceeds the 64 MiB post-download verification limit. Output is retained locally; no content was returned.",
        502,
      );
    }
  }
  return `Verified ${entries.length} local file(s), ${totalBytes} bytes.`;
}

async function runPac(args: string[], outputDirectory?: string): Promise<string> {
  const configuredExecutable = process.env.PA_MCP_PAC_EXECUTABLE;
  if (
    configuredExecutable &&
    (!isAbsolute(configuredExecutable) || containsControlCharacter(configuredExecutable))
  ) {
    throw new ApiError(
      "PA_MCP_PAC_EXECUTABLE must be an absolute executable path, not a command line",
      400,
    );
  }
  const executable = configuredExecutable || "pac";
  try {
    const started = Date.now();
    const execute = (argv: string[], timeout: number) =>
      new Promise<string>((resolveOutput, reject) => {
        const child = execFile(
          executable,
          argv,
          {
            encoding: "utf8",
            timeout,
            maxBuffer: MAX_OUTPUT_BYTES,
            windowsHide: true,
            shell: false,
            // PAC extract-template also writes a compiled JSON companion to
            // cwd, independently of --templateFileName. Confine every export
            // artifact to the exclusively created destination directory.
            ...(outputDirectory ? { cwd: outputDirectory } : {}),
          },
          (error, stdout, stderr) => {
            if (error) {
              const commandError = new Error(error.message);
              Object.assign(commandError, error, { stdout, stderr });
              reject(commandError);
              return;
            }
            resolveOutput([stdout, stderr].filter(Boolean).join("\n").trim());
          },
        );
        // A headless tool must not hang waiting for interactive answers.
        child.stdin?.end();
      });
    // Read-only profile inventory never initiates authentication. Fail closed
    // unless PAC's documented active-profile marker is present; do not invoke
    // a tenant command that might need an interactive profile selection.
    const profileList = await execute(["auth", "list"], Math.min(10_000, TIMEOUT_MS));
    if (!/^\s*\[\d+\]\s+\*\s+/m.test(profileList)) {
      throw new AuthenticationError(
        "Copilot PAC authentication is missing or no active profile could be verified. Complete 'pac auth create' or select an existing PAC profile separately, then retry. The MCP sign_in tool does not authenticate PAC; no tenant command was run.",
      );
    }
    const remaining = TIMEOUT_MS - (Date.now() - started);
    if (remaining <= 0) throw Object.assign(new Error("PAC deadline exceeded"), { killed: true });
    const output = await execute(["copilot", ...args], remaining);
    const exportVerification = outputDirectory ? await verifyExport(outputDirectory, args[0]!) : "";
    const safe = safePacText(output);
    const truncated = safe.length > MAX_RETURN_CHARS;
    return [
      safe.slice(0, MAX_RETURN_CHARS) || "PAC command completed successfully (no output).",
      truncated
        ? "CLI output truncated at 24,000 characters; this is not a complete inventory."
        : "",
      outputDirectory
        ? `Local export directory: ${outputDirectory}. The tenant was not modified.`
        : "",
      exportVerification,
      "PAC uses its own active authentication profile; MCP sign-in and switch_environment do not change that profile.",
    ]
      .filter(Boolean)
      .join("\n");
  } catch (error) {
    if (error instanceof AuthenticationError || error instanceof ApiError) throw error;
    const err = error as NodeJS.ErrnoException & {
      stdout?: string;
      stderr?: string;
      killed?: boolean;
    };
    const partial = outputDirectory
      ? " A fresh local output directory was created; any partial export is retained. Use a different directory when retrying."
      : "";
    if (err.code === "ENOENT")
      throw new ApiError(
        "Power Platform CLI (pac) is unavailable. Install Microsoft's PAC CLI or configure the host-only PA_MCP_PAC_EXECUTABLE absolute path. Authenticate the PAC profile separately; this tool never starts sign-in." +
          partial,
        503,
      );
    if (err.killed)
      throw new ApiError(
        "PAC Copilot command exceeded its 60-second deadline. No retry was attempted." + partial,
        504,
      );
    const details = sanitizeErrorMessage(
      safePacText([err.stderr, err.stdout, err.message].filter(Boolean).join("\n")),
    ).slice(0, 8000);
    if (/No profiles were found on this computer/i.test(details)) {
      throw new AuthenticationError(
        "Copilot PAC authentication is missing: no PAC profiles exist. Complete 'pac auth create' separately, then retry. The MCP sign_in tool does not authenticate PAC; this tool never starts sign-in." +
          partial,
        details,
      );
    }
    throw new ApiError(
      "PAC Copilot command failed. Check the installed command version and existing PAC profile; no sign-in or retry was attempted." +
        partial,
      500,
      details,
    );
  }
}

type PacCopilotHandler = (context: unknown, input: unknown) => Promise<string>;
export const pacCopilotHandlers: Record<string, PacCopilotHandler> = {
  pac_copilot_list: async (_context, input) => {
    const parsed = pacCopilotListSchema.parse(input);
    return runPac(["list", "--environment", validateEnvironment(parsed.environment)]);
  },
  pac_copilot_extract_template: async (_context, input) => {
    const parsed = pacCopilotExtractTemplateSchema.parse(input);
    if (parsed.confirmDownload !== true)
      throw new ConfirmationRequiredError(
        "Downloading a local Copilot template requires confirmDownload=true",
      );
    const environment = validateEnvironment(parsed.environment);
    const directory = await createExportDirectory(parsed.outputDirectory);
    return runPac(
      [
        "extract-template",
        "--environment",
        environment,
        "--bot",
        parsed.agentId,
        "--templateFileName",
        join(directory, "agent.yaml"),
        "--templateName",
        "mcpExportTemplate",
        "--templateVersion",
        "1.0.0",
      ],
      directory,
    );
  },
  pac_copilot_extract_translation: async (_context, input) => {
    const parsed = pacCopilotExtractTranslationSchema.parse(input);
    if (parsed.confirmDownload !== true)
      throw new ConfirmationRequiredError(
        "Downloading local Copilot translations requires confirmDownload=true",
      );
    const environment = validateEnvironment(parsed.environment);
    const directory = await createExportDirectory(parsed.outputDirectory);
    const args = [
      "extract-translation",
      "--environment",
      environment,
      "--bot",
      parsed.agentId,
      "--outdir",
      directory,
      "--format",
      parsed.format ?? "json",
    ];
    if (parsed.allLanguages) args.push("--all");
    return runPac(args, directory);
  },
};
